import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { Navbar } from "@/components/shop/Navbar";
import { Hero } from "@/components/shop/Hero";
import { Marquee } from "@/components/shop/Marquee";
import { Catalog } from "@/components/shop/Catalog";
import { PromoSection } from "@/components/shop/PromoSection";
import { Editorial } from "@/components/shop/Editorial";
import { QuoteSection } from "@/components/shop/QuoteSection";
import { Footer } from "@/components/shop/Footer";
import { QuickView } from "@/components/shop/QuickView";
import { CartDrawer } from "@/components/shop/CartDrawer";
import { CheckoutModal } from "@/components/shop/CheckoutModal";
import { WhatsAppButton } from "@/components/shop/WhatsAppButton";
import { AdminPage } from "@/components/shop/AdminPage";
import { PaymentResult } from "@/components/shop/PaymentResult";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hylasu-cart")) || []; }
    catch { return []; }
  });
  const [giftWrap, setGiftWrap] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [quickView, setQuickView] = useState(null);
  const [filter, setFilter] = useState("todas");
  const [dark, setDark] = useState(() => localStorage.getItem("hylasu-theme") === "dark");

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("hylasu-theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    localStorage.setItem("hylasu-cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, {
        id: product.id,
        name: product.name,
        baseName: product.baseName || product.name,
        price: product.price,
        image: product.image,
        qty,
        bundle: product.bundle || null,
      }];
    });
    toast.success(`Añadido a tu carrito: ${product.name}`);
  }, []);

  // Pack cruzado: si todas las líneas comparten el mismo pack (mismo qty y precio),
  // el pack se aplica al total combinado aunque sean colores distintos.
  const cartPriced = useMemo(() => {
    const bundles = cart.map((i) => i.bundle);
    const allSame = cart.length > 1
      && bundles.every(Boolean)
      && bundles.every((b) => b.qty === bundles[0].qty && b.price === bundles[0].price);
    let subtotal = 0;
    let packs = 0;
    let bundle = null;
    if (allSame) {
      bundle = bundles[0];
      const totalQty = cart.reduce((s, i) => s + i.qty, 0);
      packs = Math.floor(totalQty / bundle.qty);
      if (packs > 0) {
        let leftovers = packs * bundle.qty;
        const parts = [];
        const items = cart.map((item) => {
          const covered = Math.min(item.qty, leftovers);
          leftovers -= covered;
          const rest = item.qty - covered;
          if (covered) parts.push(`${item.name} x${covered}`);
          subtotal += rest * item.price;
          return { ...item, packApplied: covered > 0 };
        });
        subtotal += packs * bundle.price;
        return { items, subtotal, groupPack: { ...bundle, packs, label: parts.join(" + ") } };
      }
    }
    const items = cart.map((item) => {
      if (item.bundle && item.qty >= item.bundle.qty) {
        const packs = Math.floor(item.qty / item.bundle.qty);
        const rest = item.qty % item.bundle.qty;
        subtotal += packs * item.bundle.price + rest * item.price;
        return { ...item, packApplied: true };
      }
      subtotal += item.price * item.qty;
      return { ...item, packApplied: false };
    });
    return { items, subtotal, groupPack: null };
  }, [cart]);

  const subtotal = cartPriced.subtotal;

  const updateQty = useCallback((id, delta) => {
    setCart((prev) => prev
      .map((i) => i.id === id ? { ...i, qty: Math.max(0, i.qty + delta) } : i)
      .filter((i) => i.qty > 0));
  }, []);

  const removeItem = useCallback((id) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const promoProducts = useMemo(() => products.filter((p) => p.promo), [products]);

  const goToPromos = useCallback(() => {
    document.getElementById("promociones")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const goToCatalog = useCallback((f) => {
    setFilter(f);
    document.getElementById("catalogo")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleOrderComplete = useCallback(() => {
    setCart([]);
    setGiftWrap(false);
    setCheckoutOpen(false);
  }, []);

  if (window.location.pathname.startsWith("/admin")) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] text-stone-900 antialiased dark:bg-[#0C0A09] dark:text-stone-100">
        <Toaster position="bottom-right" richColors theme={dark ? "dark" : "light"} />
        <AdminPage />
      </div>
    );
  }

  if (window.location.pathname.startsWith("/pago-")) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] text-stone-900 antialiased dark:bg-[#0C0A09] dark:text-stone-100">
        <Toaster position="bottom-right" richColors theme={dark ? "dark" : "light"} />
        <PaymentResult />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-900 antialiased transition-colors duration-500 dark:bg-[#0C0A09] dark:text-stone-100">
      <Toaster position="bottom-right" richColors theme={dark ? "dark" : "light"} />
      <Navbar
        cartCount={cartCount}
        onCartOpen={() => setCartOpen(true)}
        onNavigate={goToCatalog}
        onPromos={goToPromos}
        dark={dark}
        onToggleTheme={() => setDark((d) => !d)}
      />
      <main>
        <Hero onExplore={() => goToCatalog("todas")} onPromos={goToPromos} />
        <Marquee />
        <Catalog
          products={products}
          filter={filter}
          onFilterChange={setFilter}
          onQuickView={setQuickView}
          onAdd={addToCart}
        />
        <PromoSection products={promoProducts} onQuickView={setQuickView} onAdd={addToCart} />
        <Editorial />
        <QuoteSection />
      </main>
      <Footer />
      <WhatsAppButton />
      <QuickView
        product={quickView}
        onClose={() => setQuickView(null)}
        onAdd={(p, qty) => { addToCart(p, qty); setQuickView(null); }}
      />
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cartPriced.items}
        subtotal={subtotal}
        groupPack={cartPriced.groupPack}
        giftWrap={giftWrap}
        onGiftWrapChange={setGiftWrap}
        onUpdateQty={updateQty}
        onRemove={removeItem}
        onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }}
      />
      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        cart={cart}
        subtotal={subtotal}
        giftWrap={giftWrap}
        api={API}
        onComplete={handleOrderComplete}
      />
    </div>
  );
}

export default App;
